import { observer, useLocalObservable } from "mobx-react";
import React, { createContext, ReactNode, useEffect, useRef } from "react";
import NavTablesStore from "./navTablesStore";
import RxNavigationPane from "../../components/NavigationPanes/RxNavigationPane";
import PatientNavigationPane from "../../components/NavigationPanes/PatientNavigationPane";
import useStore from "../../helpers/useStore";
import { useHotkey } from "../../hotkeys";
import { CalendarCommands } from "../Calendar/calendar.commands";
import { CalendarContext } from "../Calendar/calendar.provider";
import { PatientsContext } from "../Patients/patients.provider";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../Auth/auth.provider";

interface NavTablesProviderProps {
  children: ReactNode;
}

export const NavTablesContext = createContext<NavTablesStore | null>(null);
NavTablesContext.displayName = "NavTablesContext";

export const NavTablesProvider = observer(({ children }: NavTablesProviderProps) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const store = useLocalObservable(() => new NavTablesStore());
  const authStore = useStore(AuthContext);
  const isAuthenticated = !!authStore.authToken;

  // use hotkeys for opening the navigation panes
  const CalendarStore = useStore(CalendarContext);
  const { navigateToCalendar } = CalendarCommands(CalendarStore);

  const PatientStore = useStore(PatientsContext);

  const { setPatientNavTableIsOpen, setIncomingRx } = store;
  const location = useLocation();

  const latestPath = useRef(location.pathname);
  useEffect(() => {
    latestPath.current = location.pathname;
  }, [location.pathname]);

  //TODO figure out which übersicht to open depending on the context

  useHotkey({
    combo: "ü",
    scope: "global",
    description: "Patienten- bzw. Rezept-Navigation öffnen",
    handler: () => {
      // alert("Hello World from Patient Navigation Pane!");
      if (latestPath.current.startsWith("/patient")) {
        const patientData = PatientStore.openPatients.find(
          (p) => p.selectedRxId === PatientStore.selectedRxId,
        );
        setIncomingRx(PatientStore.selectedRxId!, patientData?.lastName!);
      } else if (latestPath.current.startsWith("/termin")) {
        const calendarPatientLastName = CalendarStore.selectedPatientLastName;
        setIncomingRx(CalendarStore.selectedRxId!, calendarPatientLastName!);
      } else {
        setPatientNavTableIsOpen(true);
      }
    },

    priority: 100,
  });

  return (
    <NavTablesContext.Provider value={store}>
      {isAuthenticated && <PatientNavigationPane />}
      {isAuthenticated && <RxNavigationPane />}
      {children}
    </NavTablesContext.Provider>
  );
});
